import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { PaymentMethodName } from '../../content/enums/payment/payment-method-name';
import { PaymentData } from '../../content/interfaces/payment/payment-data';
import { PaymentDataService } from '../../content/services/payment-data.service';
import { AuthService } from '../../core/services/auth.service';
import { I18nService } from '../../core/services/i18n.service';
import { RuntimeConfigService } from '../../core/services/runtime-config.service';
import { SpsMenuItem } from '../interfaces/sps-menu-item';

@Injectable({
  providedIn: 'root'
})
export class SidebarMenuService {

  menuItems: SpsMenuItem[] = [];
  paymentMethodMenuItem: SpsMenuItem = null;
  activeParentMenu: SpsMenuItem = null;
  activeChildMenu: SpsMenuItem = null;

  private menuItemRefreshed = new Subject<boolean>();
  menuItemRefreshedHandler = this.menuItemRefreshed.asObservable();

  constructor(private authService: AuthService,
              private i18n: I18nService,
              private paymentDataService: PaymentDataService,
              private runtimeConfig: RuntimeConfigService) {
  }

  /**
   * Init sidebar menu
   * @param activeMenuId
   */
  initSidebarMenu(activeMenuId: string = 'm-dashboard'): void {

    let linkIndex: number = 0;
    this.menuItems = [];

    /**
     * Dashboard menu
     */
    this.menuItems.push({
      id: 'm-dashboard',
      type: 'M',
      linkIndex: linkIndex,
      i18nLabelKey: 'MENUDASHBOARD',
      label: this.i18n.i18nLng()['MENUDASHBOARD'],
      icon: 'pi pi-home',
      routerLink: '/dashboard',
      active: (activeMenuId === 'm-dashboard'),
      selected: (activeMenuId === 'm-dashboard')
    });
    linkIndex++;

    /**
     * Users and Super-Admin have payment and transaction permissions
     * individual permissions for spk and rz users with LEAD AND PAYMENT OR TRANS roles
     */
    let paymentActivated: boolean = false;
    let transActivated: boolean = false;

    if (this.authService.userHasRole('LEAD') && this.authService.userHasRole('PAYMENT')) {
      paymentActivated = true;
    }
    if (this.authService.userHasRole('LEAD') && this.authService.userHasRole('TRANS')) {
      transActivated = true;
    }
    /**
     * Users and Super-Admin always have payment and trans permissions
     */
    if (this.authService.userHasRoleInList(['USER', 'SUPER-ADMIN'])) {
      paymentActivated = true;
      transActivated = true;
    }

    /**
     * Payment Menus
     */
    if (paymentActivated) {

      /**
       * Payment methods menu
       */
      this.paymentMethodMenuItem = {
        id: 'm-payment-methods',
        type: 'P',
        linkIndex: linkIndex++,
        nextCollapsedLinkIndex: linkIndex + 2,
        i18nLabelKey: 'MENUPAYMENTTYPES',
        label: this.i18n.i18nLng()['MENUPAYMENTTYPES'],
        icon: 'pi pi-wallet',
        active: (activeMenuId === 'm-payment-methods'),
        selected: (activeMenuId === 'm-payment-methods'),
        items: [
          {
            id: 'm-payment-methods-view',
            type: 'S',
            linkIndex: linkIndex++,
            i18nLabelKey: 'MENUPAYMENTTYPESVIEW',
            label: this.i18n.i18nLng()['MENUPAYMENTTYPESVIEW'],
            routerLink: '/paymentmethodsview',
            active: (activeMenuId === 'm-payment-methods-view'),
            selected: (activeMenuId === 'm-payment-methods-view'),
            parentId: 'm-payment-methods',
          }
        ]
      };
      this.menuItems.push(this.paymentMethodMenuItem);

      /**
       * Add subitems for all payment-methods
       */
      for (const paymentMethod of this.paymentDataService.paymentMethods) {
        if (paymentMethod.hasPaymentTypes) {
          linkIndex = this.addPaymentMenuItems(this.paymentMethodMenuItem, paymentMethod.name, linkIndex);
        }
      }
      this.paymentMethodMenuItem.nextCollapsedLinkIndex = linkIndex;

      /**
       * Payment Page
       */
      this.menuItems.push({
        id: 'm-payment-page',
        type: 'M',
        linkIndex: linkIndex++,
        previousParentId: 'm-payment-methods',
        i18nLabelKey: 'MENUPAYMENTPAGE',
        label: this.i18n.i18nLng()['MENUPAYMENTPAGE'],
        icon: 'pi pi-desktop',
        routerLink: '/paymentpage',
        active: (activeMenuId === 'm-payment-page'),
        selected: (activeMenuId === 'm-payment-page')
      });

      /**
       * Girocode menu
       */

      /* TODO GiroCode is currently deactivated maybe in future use...

      this.menuItems.push({
        id: 'm-girocode',
        type: 'M',
        linkIndex: linkIndex++,
        i18nLabelKey: 'MENUGIROCODE',
        label: this.i18n.i18nLng()['MENUGIROCODE'],
        icon: 'pi pi-qrcode',
        routerLink: '/girocode',
        active: (activeMenuId === 'm-girocode'),
        selected: (activeMenuId === 'm-girocode')
      });

       */

    }

    /**
     * Transaction Menu
     */
    if (transActivated) {
      /**
       * Transactions menu
       */
      this.menuItems.push({
        id: 'm-transactions',
        type: 'M',
        linkIndex: linkIndex++,
        i18nLabelKey: 'MENUTRANSACTIONS',
        label: this.i18n.i18nLng()['MENUTRANSACTIONS'],
        icon: 'pi pi-arrow-right-arrow-left',
        routerLink: '/transactionsview',
        active: (activeMenuId === 'm-transactions'),
        selected: (activeMenuId === 'm-transactions')
      });

    }

    /**
     * Menu for payout files
     */
    if (paymentActivated || transActivated) {
      this.menuItems.push({
        id: 'm-payout-files',
        type: 'P',
        linkIndex: linkIndex++,
        nextCollapsedLinkIndex: linkIndex + 3,
        i18nLabelKey: 'MENUPAYOUTFILES',
        label: this.i18n.i18nLng()['MENUPAYOUTFILES'],
        icon: 'pi pi-euro',
        active: (activeMenuId === 'm-payout-files'),
        selected: (activeMenuId === 'm-payout-files'),
        items: [
          {
            id: 'm-payout-creditcard',
            type: 'S',
            linkIndex: linkIndex++,
            i18nLabelKey: 'CREDITCARD',
            label: this.i18n.i18nLng()['CREDITCARD'],
            routerLink: '/payoutcc',
            active: (activeMenuId === 'm-payout-creditcard'),
            selected: (activeMenuId === 'm-payout-creditcard'),
            parentId: 'm-payout-files',
          },
          {
            id: 'm-payout-sepa',
            type: 'S',
            linkIndex: linkIndex++,
            i18nLabelKey: 'SEPA',
            label: this.i18n.i18nLng()['SEPA'],
            routerLink: '/payoutsepa',
            active: (activeMenuId === 'm-payout-sepa'),
            selected: (activeMenuId === 'm-payout-sepa'),
            parentId: 'm-payout-files',
          },
          {
            id: 'm-payout-wero',
            type: 'S',
            linkIndex: linkIndex++,
            i18nLabelKey: 'WERO',
            label: this.i18n.i18nLng()['WERO'],
            routerLink: '/payoutwero',
            active: (activeMenuId === 'm-payout-wero'),
            selected: (activeMenuId === 'm-payout-wero'),
            parentId: 'm-payout-files',
          },
        ]
      });
    }

    /**
     * Help menu
     */
    this.menuItems.push({
      id: 'm-help',
      type: 'P',
      linkIndex: linkIndex++,
      nextCollapsedLinkIndex: linkIndex + 4,
      previousParentId: (paymentActivated || transActivated) ? 'm-payout-files' : undefined,
      i18nLabelKey: 'MENUHELP',
      label: this.i18n.i18nLng()['MENUHELP'],
      icon: 'pi pi-question-circle',
      active: (activeMenuId === 'm-help'),
      selected: (activeMenuId === 'm-help'),
      items: [
        {
          id: 'm-help-faq',
          type: 'S',
          linkIndex: linkIndex++,
          i18nLabelKey: 'MENUFAQ',
          label: this.i18n.i18nLng()['MENUFAQ'],
          icon: 'pi pi-external-link',
          url: this.runtimeConfig.faqLink,
          // routerLink: '/helpfaq',
          active: (activeMenuId === 'm-help-faq'),
          selected: (activeMenuId === 'm-help-faq'),
          parentId: 'm-help',
        },
        {
          id: 'm-help-contact',
          type: 'S',
          linkIndex: linkIndex++,
          i18nLabelKey: 'MENUCONTACT',
          label: this.i18n.i18nLng()['MENUCONTACT'],
          routerLink: '/helpcontact',
          active: (activeMenuId === 'm-help-contact'),
          selected: (activeMenuId === 'm-help-contact'),
          parentId: 'm-help',
        },
        {
          id: 'm-help-api',
          type: 'S',
          linkIndex: linkIndex++,
          i18nLabelKey: 'MENUAPI',
          icon: 'pi pi-external-link',
          label: this.i18n.i18nLng()['MENUAPI'],
          url: this.runtimeConfig.apiReferenceLink,
          active: (activeMenuId === 'm-help-api'),
          selected: (activeMenuId === 'm-help-api'),
          parentId: 'm-help',
        },
        {
          id: 'm-help-manual',
          type: 'S',
          linkIndex: linkIndex++,
          i18nLabelKey: 'MENUMANUAL',
          icon: 'pi pi-external-link',
          label: this.i18n.i18nLng()['MENUMANUAL'],
          url: this.runtimeConfig.userManualLink,
          active: (activeMenuId === 'm-help-manual'),
          selected: (activeMenuId === 'm-help-manual'),
          parentId: 'm-help',
        },
      ]
    });

    if (this.authService.userHasRole('SUPER-ADMIN')) {
      /**
       * Administration menu
       */
      this.menuItems.push({
        id: 'm-administration',
        type: 'P',
        linkIndex: linkIndex++,
        previousParentId: 'm-help',
        i18nLabelKey: 'MENUADMIN',
        label: this.i18n.i18nLng()['MENUADMIN'],
        icon: 'pi pi-cog',
        active: (activeMenuId === 'm-admin'),
        selected: (activeMenuId === 'm-admin'),
        items: [
          {
            id: 'm-admin-customers',
            type: 'S',
            linkIndex: linkIndex++,
            i18nLabelKey: 'MENUCUSTOMERS',
            label: this.i18n.i18nLng()['MENUCUSTOMERS'],
            routerLink: '/admincustomers',
            active: (activeMenuId === 'm-admin-customers'),
            selected: (activeMenuId === 'm-admin-customers'),
            parentId: 'm-administration',
          },
          {
            id: 'm-admin-onboarding',
            type: 'S',
            linkIndex: linkIndex++,
            i18nLabelKey: 'MENUONBOARDING',
            label: this.i18n.i18nLng()['MENUONBOARDING'],
            routerLink: '/adminonboarding',
            active: (activeMenuId === 'm-admin-onboarding'),
            selected: (activeMenuId === 'm-admin-onboarding'),
            parentId: 'm-administration',
          },
        ]
      });
    }

    this.activeParentMenu = null;
    this.activeChildMenu = this.menuItems.find(menuItem => menuItem.active === true);
  }

  /**
   * Add MenuItems dynamically
   * @param parentMenu
   * @param paymentMethod
   * @param linkIndex
   * @param refresh
   */
  addPaymentMenuItems(parentMenu: SpsMenuItem, paymentMethod: PaymentMethodName, linkIndex: number, refresh: boolean = false): number {

    let paymentTypes: PaymentData[] = [];

    switch (paymentMethod) {
      case PaymentMethodName.PAYPAL:
        parentMenu.items.push(
          {
            id: 'm-payment-header-paypal',
            type: 'H',
            label: 'PAYPAL'
          }
        );
        paymentTypes = this.paymentDataService.paymentTypes.filter(item => item.paymentMethod === PaymentMethodName.PAYPAL);
        break;
      case PaymentMethodName.APPLEPAY:
        parentMenu.items.push(
          {
            id: 'm-payment-header-applepay',
            type: 'H',
            label: 'APPLE PAY'
          }
        );
        paymentTypes = this.paymentDataService.paymentTypes.filter(item => item.paymentMethod === PaymentMethodName.APPLEPAY);
        break;
      case PaymentMethodName.CREDITCARD:
        parentMenu.items.push(
          {
            id: 'm-payment-header-cc',
            type: 'H',
            label: (this.i18n.i18nLng()['CREDITCARD']).toUpperCase(),
            i18nLabelKey: 'CREDITCARD'
          }
        );
        paymentTypes = this.paymentDataService.paymentTypes.filter(item => item.paymentMethod === PaymentMethodName.CREDITCARD);
        break;
      case PaymentMethodName.CREDITCARD_MOTO:
        parentMenu.items.push(
          {
            id: 'm-payment-header-cc-moto',
            type: 'H',
            label: (this.i18n.i18nLng()['CREDITCARD_MOTO']).toUpperCase(),
            i18nLabelKey: 'CREDITCARD_MOTO'
          }
        );
        paymentTypes = this.paymentDataService.paymentTypes.filter(item => item.paymentMethod === PaymentMethodName.CREDITCARD_MOTO);
        break;
      case PaymentMethodName.SEPA:
        parentMenu.items.push(
          {
            id: 'm-payment-header-sepa',
            type: 'H',
            label: (this.i18n.i18nLng()['SEPA']).toUpperCase(),
            i18nLabelKey: 'SEPA'
          }
        );
        paymentTypes = this.paymentDataService.paymentTypes.filter(item => item.paymentMethod === PaymentMethodName.SEPA);
        break;
      case PaymentMethodName.SEPA_BLACKLIST:
        parentMenu.items.push(
          {
            id: 'm-payment-header-sepa-blacklist',
            type: 'H',
            label: (this.i18n.i18nLng()['SEPA_BLACKLIST']).toUpperCase(),
            i18nLabelKey: 'SEPA_BLACKLIST_SHORT'
          }
        );
        paymentTypes = this.paymentDataService.paymentTypes.filter(item => item.paymentMethod === PaymentMethodName.SEPA_BLACKLIST);
        break;
      case PaymentMethodName.GOOGLEPAY:
        parentMenu.items.push(
          {
            id: 'm-payment-header-googlepay',
            type: 'H',
            label: (this.i18n.i18nLng()['GOOGLEPAY']).toUpperCase(),
            i18nLabelKey: 'GOOGLEPAY'
          }
        );
        paymentTypes = this.paymentDataService.paymentTypes.filter(item => item.paymentMethod === PaymentMethodName.GOOGLEPAY);
        break;
      case PaymentMethodName.KLARNA:
        parentMenu.items.push(
          {
            id: 'm-payment-header-klarna',
            type: 'H',
            label: (this.i18n.i18nLng()['KLARNA']).toUpperCase(),
            i18nLabelKey: 'KLARNA'
          }
        );
        paymentTypes = this.paymentDataService.paymentTypes.filter(item => item.paymentMethod === PaymentMethodName.KLARNA);
        break;
      case PaymentMethodName.DIRECTTRANSFER:
        parentMenu.items.push(
          {
            id: 'm-payment-header-direkttransfer',
            type: 'H',
            label: (this.i18n.i18nLng()['DIRECTTRANSFER']).toUpperCase(),
            i18nLabelKey: 'DIRECTTRANSFER'
          }
        );
        paymentTypes = this.paymentDataService.paymentTypes.filter(item => item.paymentMethod === PaymentMethodName.DIRECTTRANSFER);
        break;
      case PaymentMethodName.WERO:
        parentMenu.items.push(
          {
            id: 'm-payment-header-wero',
            type: 'H',
            label: (this.i18n.i18nLng()['WERO']).toUpperCase(),
            i18nLabelKey: 'WERO'
          }
        );
        paymentTypes = this.paymentDataService.paymentTypes.filter(item => item.paymentMethod === PaymentMethodName.WERO);
        break;
    }
    paymentTypes.forEach((paymentType: PaymentData) => {
      const id = 'm-pm-' + paymentType.id;
      const active = (refresh && this.activeChildMenu.id === id);
      parentMenu.items.push(
        {
          id: id,
          type: 'SP',
          linkIndex: linkIndex++,
          label: paymentType.title,
          routerLink: '/paymentmethod/' + paymentType.id,
          active: active,
          selected: active,
          data: paymentType,
          parentId: parentMenu.id,
        }
      );
      /**
       * refresh activeChildMenu (only in refresh-mode needed)
       */
      if (refresh && active) {
        this.activeChildMenu = parentMenu.items.find(item => item.id === id);
      }
    });
    return linkIndex;

  }

  /**
   * Find MenuItem by id - recursive
   * @param menuItems
   * @param id
   */
  findMenuItemById(menuItems: SpsMenuItem[], id: string): SpsMenuItem | null {
    for (const item of menuItems) {
      if (item.id === id) {
        return item;
      }
      if (item.items) {
        const found = this.findMenuItemById(item.items, id);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  /**
   * Payment-Methods list has changed
   * refresh the sidebar-menu by refreshing the
   * menu-array and trigger the observable
   */
  refreshPaymentMenuItems() {
    let linkIndex: number = this.paymentMethodMenuItem.linkIndex + 2;
    /**
     * remove all payment-method items, except all payments view
     */
    this.paymentMethodMenuItem.items = this.paymentMethodMenuItem.items.filter(item => item.id === 'm-payment-methods-view');
    /**
     * Add subitems for all payment-methods
     */
    for (const paymentMethod of this.paymentDataService.paymentMethods) {
      if (paymentMethod.hasPaymentTypes) {
        linkIndex = this.addPaymentMenuItems(this.paymentMethodMenuItem, paymentMethod.name, linkIndex, true);
      }
    }
    this.paymentMethodMenuItem.nextCollapsedLinkIndex = linkIndex;
    /**
     * Refresh following menuitems  linkindex
     * TODO eventually globalize function if needed
     */
    try {
      this.menuItems.find(item => item.id === 'm-payment-page').linkIndex = linkIndex++;
    } catch (e) {
    }
    try {
      this.menuItems.find(item => item.id === 'm-transactions').linkIndex = linkIndex++;
    } catch (e) {
    }
    /**
    try {
      this.menuItems.find(item => item.id === 'm-girocode').linkIndex = linkIndex++;
    } catch (e) {
    }
     */
    try {
      const helpMenu: SpsMenuItem = this.menuItems.find(item => item.id === 'm-help');
      if (helpMenu) {
        helpMenu.linkIndex = linkIndex++;
        helpMenu.nextCollapsedLinkIndex = linkIndex + 4;
        helpMenu.items.map(helpItem => {
          helpItem.linkIndex = linkIndex++;
        });
      }
    } catch (e) {
    }
    try {
      const adminMenu: SpsMenuItem = this.menuItems.find(item => item.id === 'm-administration');
      adminMenu.linkIndex = linkIndex++;
      adminMenu.items.map(adminItem => {
        adminItem.linkIndex = linkIndex++;
      });
    } catch (e) {
    }

    /**
     * inform other components and services that subscribe on refresh
     */
    this.menuItemRefreshed.next(true)
  }

  /**
   * change the label of a payment-method menu
   * if data has been edited
   * @param menuId
   * @param title
   */
  changeMenuLabel(menuId: string, title: string) {
    let childMenu = this.paymentMethodMenuItem.items.find(item => item.id === menuId);
    if (childMenu) {
      childMenu.label = title;
    }
  }

  /**
   * change the label of a payment-method menu
   * if data has been edited
   * @param menuId
   * @param status
   */
  changeMenuStatus(menuId: string, status: 'LIVE' | 'TEST' | 'CLOSED' | 'DELETED') {
    let childMenu = this.paymentMethodMenuItem.items.find(item => item.id === menuId);
    if (childMenu) {
      childMenu.data.status = status;
    }
  }

}
